import React from "react";
import { Link } from "react-router-dom";
import { Star, Quote, Heart, ArrowUpRight } from "lucide-react";
import { RevealText, Reveal } from "@/components/Reveal";
import { TESTIMONIALS, IMAGES, REVIEW_LINKS } from "@/data/content";

// Client love — real reviews from actual clients
const REVIEWS = [
  {
    quote:
      "We decided to go with Jacqueline to do our twins senior pics, they hate taking pics and the fuss of it all. They showed up and Jacqueline was there, and had a plan for the boys to get their pics done, it was very efficient and easy! The boys even said, \u201Cthat wasn\u2019t bad at all!\u201D She had our pictures ready before we expected them which is a plus and they came out really great!! We will definitely use her again!",
    name: "P. Painter",
  },
  {
    quote:
      "We\u2019ve just completed our second session with Jacqueline. We have had such a smooth experience both times and I have little ones so pictures aren\u2019t always easy. The pictures come out beautifully! Highly recommend!",
    name: "A. Airhart",
  },
  {
    quote:
      "Jac is amazing! She captured my little girl perfectly for her personality portraits and now she\u2019s all I want to use! I love that my little one feels comfortable with her, makes picture taking so easy! Thank you for giving us the best memories!!",
    name: "A. Becker",
  },
  {
    quote:
      "I used Jacqueline for our family photos. She is great, she captured the personality\u2019s of each of us. She had my grandkids laughing, which captured the cutest shots! There wasn\u2019t one photo I didn\u2019t love!",
    name: "K. Helms",
  },
  {
    quote:
      "Jacqueline managed to capture a ton of beautiful photos for our mini session in only about ten minutes! She was perfect with our small children! Highly recommend!",
    name: "A. Devine",
  },
  {
    quote:
      "This was my first ever photography session and I was so nervous and awkward going into it. Jacqueline really helped us capture these beautiful moments and made me feel comfortable while doing it. She was so patient and kind and I\u2019m completely blown away by our session. I will definitely be booking with her for my babies photos and I can\u2019t wait! Thank you so much again for bringing my expectations to reality!!",
    name: "T. Harrison",
  },
  {
    quote:
      "I had a fantastic experience with Jacqueline! She was able to maximize the time of our mini session and take photos that we will treasure forever. Despite the obstacles of rain, puddles, bugs, sweaty adults, antsy children, etc... she was still able to capture our natural happiness and love. I\u2019m so grateful for her talent, time, and care. I would easily recommend Jacqueline Claire Photography to anyone in the area searching for quality images for any occasion.",
    name: "R. Bennett",
  },
  {
    quote:
      "I had a fantastic experience with Jacqueline! She was able to maximize the time of our mini session and take photos that we will treasure forever. Despite the obstacles of rain, puddles, bugs, sweaty adults, antsy children, etc... she was still able to capture our natural happiness and love. I\u2019m so grateful for her talent, time, and care. I would easily recommend Jacqueline Claire Photography to anyone in the area searching for quality images for any occasion.",
    name: "T. Melancon",
  },
  {
    quote:
      "She was amazing. Patient. Up for challenges and made us laugh too. And in an hour she made magic happen. Definitely using her again.",
    name: "A. Wiltz",
  },
  {
    quote:
      "Highly recommend. Jacqueline was super nice and laid back. The photos were quickly and beautifully edited.",
    name: "E. Guidry",
  },
  {
    quote:
      "We had a great extended family session with Jacqueline! She\u2019s personable and easy to work with! I liked her posing and editing. Can\u2019t wait to have another shoot!",
    name: "B. Bonin",
  },
  {
    quote:
      "Jacqueline took my daughter\u2019s senior photos. She suggested locations and poses for each outfit. Jacqueline communicated with us from scheduling the session to editing and downloading the photos. We are extremely pleased with her services and pricing. Hope to schedule a family session with her soon!",
    name: "R. Breaux",
  },
];

export default function ClientLove() {
  return (
    <div className="pt-8 md:pt-12">
      {/* HERO */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="overline text-sage mb-6 flex items-center gap-2">
          <Heart size={14} className="fill-sage" strokeWidth={0} /> Client Love
        </p>
        <RevealText
          as="h1"
          className="font-serif font-light text-ink leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl"
          lines={["Kind words from", "kind people."]}
        />
        <Reveal delay={0.3}>
          <div className="mt-10 flex items-center gap-4">
            <div className="flex gap-1 text-sage">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} fill="currentColor" strokeWidth={0} />
              ))}
            </div>
            <p className="font-serif italic text-inksoft text-lg md:text-xl">
              Five-star rated · Eighteen years photographing
            </p>
          </div>
          <p className="mt-6 max-w-2xl text-inksoft leading-relaxed">
            Eighteen years of quiet mornings, big-family sunsets, hospital hellos, and senior
            year send-offs — the moments below say it best.
          </p>
        </Reveal>
      </section>

      {/* REVIEWS GRID — asymmetric editorial masonry */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {REVIEWS.map((r, i) => (
            <Reveal
              key={i}
              delay={(i % 3) * 0.08}
              className={`${i % 5 === 0 ? "lg:mt-14" : ""} ${i % 5 === 2 ? "lg:mt-8" : ""}`}
            >
              <div
                data-testid={`review-card-${i}`}
                className="relative h-full rounded-tl-[2.5rem] border border-ink/10 bg-cream/60 backdrop-blur p-8 md:p-10 hover:border-sage/60 transition-colors"
              >
                <Quote size={26} className="text-sage/60" strokeWidth={1.4} />
                <p className="mt-5 font-serif italic text-ink text-xl md:text-2xl leading-snug">
                  &ldquo;{r.quote}&rdquo;
                </p>
                <div className="mt-8 flex items-center justify-between">
                  <p className="overline text-inkmuted">{r.name}</p>
                  <div className="flex gap-0.5 text-sage">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} size={12} fill="currentColor" strokeWidth={0} />
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHAT'S NEXT — dual paths for past clients + prospects */}
      <section className="bg-sagelight py-24 md:py-32">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14 md:divide-x md:divide-ink/15">
            {/* Left column — past clients */}
            <div className="md:pr-16">
              <p className="overline text-sage mb-4">{REVIEW_LINKS.eyebrow}</p>
              <h2 className="font-serif font-light text-ink text-3xl md:text-4xl leading-[1.05] tracking-tight">
                {REVIEW_LINKS.title}
              </h2>
              <p className="mt-6 text-inksoft leading-relaxed">
                {REVIEW_LINKS.body}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={REVIEW_LINKS.google}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="review-google-clientlove"
                  className="group inline-flex items-center justify-between gap-3 rounded-full bg-ink text-cream px-7 py-3.5 text-sm font-medium hover:bg-sage transition-colors duration-300 whitespace-nowrap min-w-[220px]"
                >
                  Review on Google
                  <ArrowUpRight
                    size={15}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
                <a
                  href={REVIEW_LINKS.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="review-facebook-clientlove"
                  className="group inline-flex items-center justify-between gap-3 rounded-full border border-ink px-7 py-3.5 text-sm font-medium text-ink hover:bg-ink hover:text-cream transition-colors duration-300 whitespace-nowrap min-w-[220px]"
                >
                  Review on Facebook
                  <ArrowUpRight
                    size={15}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </div>
            </div>

            {/* Right column — new clients */}
            <div className="md:pl-16">
              <p className="overline text-sage mb-4">Your turn</p>
              <h2 className="font-serif font-light text-ink text-3xl md:text-4xl leading-[1.05] tracking-tight">
                Ready to make your own?
              </h2>
              <p className="mt-6 text-inksoft leading-relaxed">
                Every story on this page started with a note in my inbox or a session picked from the calendar. When you're ready, so am I.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/investment"
                  data-testid="client-love-book"
                  className="group inline-flex items-center justify-between gap-3 rounded-full bg-ink text-cream px-7 py-3.5 text-sm font-medium hover:bg-sage transition-colors duration-300 whitespace-nowrap min-w-[220px]"
                >
                  Book your session
                  <ArrowUpRight
                    size={15}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </Link>
                <Link
                  to="/portfolio"
                  data-testid="client-love-portfolio"
                  className="inline-flex items-center justify-between gap-3 rounded-full border border-ink px-7 py-3.5 text-sm font-medium text-ink hover:bg-ink hover:text-cream transition-colors duration-300 whitespace-nowrap min-w-[220px]"
                >
                  See the portfolio
                  <ArrowUpRight size={15} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
